import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Mail, Server, Lock, Check, AlertCircle } from 'lucide-react';
import { EmailConfig, createEmailConfig, updateEmailConfig } from '../../services/emailConfigService';
import { useToast } from '../../components/ui/ToastProvider';

// Validation schema
const emailConfigSchema = z.object({
  email: z.string().email('Valid email is required'),
  password: z.string().min(1, 'Password is required'),
  smtp_host: z.string().min(1, 'SMTP host is required'),
  smtp_port: z.coerce.number().int().positive('Port must be a positive number'),
  smtp_tls: z.enum(['tls', 'starttls', 'none']),
  is_default: z.boolean().default(false),
  tenant_id: z.string().optional().nullable()
});

type EmailConfigFormSchema = z.infer<typeof emailConfigSchema>;
// Ensure the type matches what we're using in the form
type FormValues = EmailConfigFormSchema;

interface EmailConfigFormProps {
  initialData?: EmailConfig;
  onSuccess?: (data: EmailConfig) => void;
  onCancel?: () => void;
  tenantId?: string;
  noForm?: boolean; // If true, don't render a form element (for use inside other forms)
}

const EmailConfigForm: React.FC<EmailConfigFormProps> = ({
  initialData,
  onSuccess,
  onCancel,
  tenantId,
  noForm = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState<{success: boolean, message: string} | null>(null);
  const toast = useToast();

  // Initialize form with default values or initial data
  const { register, handleSubmit, formState: { errors }, watch } = useForm<FormValues>({
    resolver: zodResolver(emailConfigSchema) as any,
    defaultValues: initialData || {
      email: '',
      password: '',
      smtp_host: 'smtp.office365.com',
      smtp_port: 587,
      smtp_tls: 'starttls' as const,
      is_default: false,
      tenant_id: tenantId || null
    }
  });

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setTestEmailResult(null);
    
    try {
      const emailConfigData: EmailConfig = {
        ...data,
        is_active: true
      };
      
      let response;
      if (initialData?.uuid) {
        // Update existing configuration
        response = await updateEmailConfig(initialData.uuid, emailConfigData, true);
      } else {
        // Create new configuration
        response = await createEmailConfig(emailConfigData, true);
      }
      
      // Set test email result if available
      if (response.test_result) {
        setTestEmailResult(response.test_result);
      }
      
      toast.success(initialData ? 'Email configuration updated' : 'Email configuration created');
      
      if (onSuccess) {
        onSuccess(response.email_config);
      }
    } catch (error) {
      toast.error(`Error: ${error instanceof Error ? error.message : 'Failed to save email configuration'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create a handler for form submission that can be used with a button
  const handleFormSubmit = () => {
    handleSubmit(onSubmit as any)();
  };

  // Render either a form or a div based on the noForm prop
  const FormComponent = noForm ? 'div' : 'form';
  const formProps = noForm ? 
    { className: "space-y-6" } : 
    { onSubmit: handleSubmit(onSubmit as any), className: "space-y-6" };
  
  return (
    <FormComponent {...formProps}>
      {/* Email field */}
      <div className="form-group">
        <label htmlFor="email" className="form-label flex items-center">
          <Mail size={16} className="mr-2" />
          Email Address
        </label>
        <input
          id="email"
          type="email"
          className={`form-input ${errors.email ? 'border-red-500' : ''}`}
          placeholder="email@example.com"
          {...register('email')}
        />
        {errors.email && (
          <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
        )}
      </div>

      {/* Password field */}
      <div className="form-group">
        <label htmlFor="password" className="form-label flex items-center">
          <Lock size={16} className="mr-2" />
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            className={`form-input pr-10 ${errors.password ? 'border-red-500' : ''}`}
            placeholder="Enter password"
            {...register('password')}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? (
              <EyeOff size={16} className="text-gray-400" />
            ) : (
              <Eye size={16} className="text-gray-400" />
            )}
          </button>
        </div>
        {errors.password && (
          <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
        )}
      </div>

      {/* SMTP Host field */}
      <div className="form-group">
        <label htmlFor="smtp_host" className="form-label flex items-center">
          <Server size={16} className="mr-2" />
          SMTP Host
        </label>
        <input
          id="smtp_host"
          type="text"
          className={`form-input ${errors.smtp_host ? 'border-red-500' : ''}`}
          placeholder="smtp.example.com"
          {...register('smtp_host')}
        />
        {errors.smtp_host && (
          <p className="text-red-500 text-xs mt-1">{errors.smtp_host.message}</p>
        )}
      </div>

      {/* SMTP Port field */}
      <div className="form-group">
        <label htmlFor="smtp_port" className="form-label">
          SMTP Port
        </label>
        <input
          id="smtp_port"
          type="number"
          className={`form-input ${errors.smtp_port ? 'border-red-500' : ''}`}
          placeholder="587"
          {...register('smtp_port')}
        />
        {errors.smtp_port && (
          <p className="text-red-500 text-xs mt-1">{errors.smtp_port.message}</p>
        )}
      </div>

      {/* SMTP TLS field */}
      <div className="form-group">
        <label htmlFor="smtp_tls" className="form-label">
          TLS Type
        </label>
        <select
          id="smtp_tls"
          className={`form-select ${errors.smtp_tls ? 'border-red-500' : ''}`}
          {...register('smtp_tls')}
        >
          <option value="starttls">STARTTLS (Recommended)</option>
          <option value="tls">TLS</option>
          <option value="none">None (Insecure)</option>
        </select>
        {errors.smtp_tls && (
          <p className="text-red-500 text-xs mt-1">{errors.smtp_tls.message}</p>
        )}
      </div>

      {/* Default checkbox */}
      <div className="form-group">
        <div className="flex items-center">
          <input
            id="is_default"
            type="checkbox"
            className="form-checkbox h-4 w-4"
            {...register('is_default')}
          />
          <label htmlFor="is_default" className="ml-2 block text-sm">
            Set as default email configuration
          </label>
        </div>
      </div>

      {/* Test email result */}
      {testEmailResult && (
        <div className={`p-3 rounded-md ${
          testEmailResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          <div className="flex items-start">
            {testEmailResult.success ? (
              <Check className="h-5 w-5 mr-2" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-2" />
            )}
            <div>
              <p className="font-medium">
                {testEmailResult.success ? 'Test email sent successfully' : 'Failed to send test email'}
              </p>
              <p className="text-sm">{testEmailResult.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form actions */}
      <div className="flex justify-end space-x-3">
        {onCancel && (
          <button
            type="button"
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
        {noForm ? (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleFormSubmit(); }}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update' : 'Save'}
          </button>
        ) : (
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : initialData ? 'Update' : 'Save'}
          </button>
        )}
      </div>
    </FormComponent>
  );
};

export default EmailConfigForm;
